export type Device = {
  device_id: string;
  name: string;
  ip_address?: string;
  device_metadata?: Record<string, unknown>;
};

export type Camera = {
  camera_id: string;
  display_name?: string;
  slug?: string;
  source?: Record<string, any>;
  health?: Record<string, any>;
};

export type CameraClaimOption = {
  option_id: string;
  camera_id: string;
  display_name?: string;
  source?: Record<string, any>;
  availability?: {
    state?: string;
    reason?: string;
    checked_at?: string;
  };
  already_claimed?: boolean;
  claim_ready?: boolean;
  claim_block_reason?: string;
  claim_payload?: Record<string, any>;
};

export type RtspInlineCreds = {
  username: string;
  password: string;
};

export type JobStep = {
  step_id?: string;
  status?: string;
  error_code?: string;
  error_detail?: string;
  artifacts?: Record<string, any>;
};

export type BringUpIdentityClassification = {
  state?: string;
  reason_code?: string;
  summary?: string;
  remediation?: string[];
  reclaim_supported?: boolean;
  scope_check_mode?: string;
};

export type CommissionJob = {
  job_id?: string;
  run_id?: string;
  status?: string;
  error_code?: string;
  error_detail?: string;
  provision_mode?: string;
  steps?: JobStep[];
  validation?: {
    provision_mode?: string;
    bootstrap_complete?: boolean;
    ready_for_production?: boolean;
    preflight_passed?: boolean;
    release_gate_passed?: boolean;
    issues?: string[];
  };
};

export type RunnerProgress = {
  status?: string;
  mode?: string;
  current_step?: string | null;
  current_step_status?: string | null;
  last_progress_at?: string | null;
  idle_seconds?: number | null;
};

export type DetailFact = {
  label: string;
  value: string;
};

export type StatusNotice = {
  level: 'success' | 'warning' | 'error';
  message: string;
  facts?: DetailFact[];
  technical?: string;
};

export type GuidedRecoveryOptions = {
  deleteAfter?: boolean;
  retryBringUp?: boolean;
  closeDialog?: boolean;
  source?: 'manual' | 'auto';
};

export type BringUpCreateForm = {
  device_id: string;
  name: string;
  description: string;
  device_type: string;
  location: string;
};

export const EMPTY_BRING_UP_CREATE_FORM: BringUpCreateForm = {
  device_id: '',
  name: '',
  description: '',
  device_type: 'edge',
  location: '',
};

export const STEP_ORDER = [
  'resolve_device',
  'edge_contract_check',
  'runner_contract_check',
  'onboard',
  'software_update',
  'setup_apply',
  'camera_claims',
  'registry_deploy',
  'identity_gate',
  'desired_state_sync',
  'preflight_run',
  'commissioning_artifacts',
] as const;

export const STEP_LABELS: Record<string, string> = {
  resolve_device: 'Resolve device identity and edge target',
  edge_contract_check: 'Validate edge identity/scope contract',
  runner_contract_check: 'Validate deploy runner contract',
  onboard: 'Onboard runtime + mTLS materials',
  software_update: 'Install latest runtime (native + docker)',
  setup_apply: 'Apply edge setup and auto-fix defaults',
  camera_claims: 'Claim cameras (optional)',
  registry_deploy: 'Publish desired-state revision',
  identity_gate: 'Verify edge identity and cloud activation',
  desired_state_sync: 'Sync desired state to edge',
  preflight_run: 'Run strict preflight checks',
  commissioning_artifacts: 'Validate release/commissioning gate',
};

export const STEP_PENDING_TEXT: Record<string, string> = {
  resolve_device: 'Waiting to resolve the selected device and edge target.',
  edge_contract_check: 'Waiting to classify edge identity and runtime contract.',
  runner_contract_check: 'Waiting for edge identity classification to finish.',
  onboard: 'Waiting for identity-safe bootstrap before onboarding starts.',
  software_update: 'Waiting to see whether a runtime upgrade is required.',
  setup_apply: 'Waiting for onboarding and runtime checks to finish.',
  camera_claims: 'Waiting for base bring-up to finish.',
  registry_deploy: 'Waiting for setup and optional camera claims.',
  identity_gate: 'Waiting for edge activation and strict identity verification.',
  desired_state_sync: 'Waiting for identity gate to pass.',
  preflight_run: 'Waiting for desired state to sync to the edge.',
  commissioning_artifacts: 'Waiting for strict preflight results.',
};

export const DEFAULT_CLOUD_HOST = 'cloud.edgentik.local';
export const DEFAULT_CLOUD_API_URL = 'https://cloud.edgentik.local:8443';
export const DEFAULT_CLOUD_MQTT_PORT = '8883';

export function chipColor(status: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  const s = String(status || '').toLowerCase();
  if (s === 'succeeded' || s === 'pass') return 'success';
  if (s === 'failed' || s === 'fail') return 'error';
  if (s === 'running') return 'info';
  if (s === 'skipped' || s === 'warn') return 'warning';
  return 'default';
}

export function availabilityChipColor(state?: string): 'default' | 'success' | 'warning' | 'error' {
  const s = String(state || '').toLowerCase();
  if (s === 'available') return 'success';
  if (s === 'unavailable') return 'error';
  if (s === 'unknown') return 'warning';
  return 'default';
}

export function getStepClassification(step?: JobStep | null): BringUpIdentityClassification | null {
  const artifacts = step?.artifacts;
  const classification = artifacts?.classification;
  if (!classification || typeof classification !== 'object') return null;
  return classification as BringUpIdentityClassification;
}

export function getNestedRunnerFailureStep(step?: JobStep | null): Record<string, any> | null {
  const final = step?.artifacts?.final;
  const runnerSteps = Array.isArray(final?.steps) ? final.steps : [];
  for (let idx = runnerSteps.length - 1; idx >= 0; idx -= 1) {
    const candidate = runnerSteps[idx];
    if (!candidate || typeof candidate !== 'object') continue;
    const status = String(candidate.status || '').trim().toLowerCase();
    const rc = Number(candidate.rc);
    if (status === 'failed' || (Number.isFinite(rc) && rc !== 0)) {
      return candidate as Record<string, any>;
    }
  }
  return null;
}

export function getBringUpFailureFacts(
  job?: CommissionJob | null,
  failedStep?: JobStep | null,
): Array<{ label: string; value: string }> {
  const facts: Array<{ label: string; value: string }> = [];
  const stepId = String(failedStep?.step_id || '').trim();
  const stepLabel = stepId ? (STEP_LABELS[stepId] || stepId) : '';
  const stepErrorCode = String(failedStep?.error_code || '').trim();
  const stepErrorDetail = String(failedStep?.error_detail || '').trim();
  const nestedRunner = getNestedRunnerFailureStep(failedStep);
  const nestedRunnerStep = String(nestedRunner?.step || '').trim();
  const nestedRunnerDetail = String(
    nestedRunner?.err ||
      nestedRunner?.out ||
      failedStep?.artifacts?.final?.detail ||
      failedStep?.artifacts?.final?.error ||
      '',
  ).trim();

  if (stepLabel) facts.push({ label: 'Failed step', value: stepLabel });
  if (stepErrorCode && stepErrorCode !== String(job?.error_code || '').trim()) {
    facts.push({ label: 'Step error', value: stepErrorCode });
  }
  if (stepErrorDetail && stepErrorDetail !== String(job?.error_detail || '').trim()) {
    facts.push({ label: 'Step detail', value: stepErrorDetail });
  }
  if (nestedRunnerStep) {
    facts.push({ label: 'Runner step', value: nestedRunnerStep });
  }
  if (nestedRunnerDetail) {
    facts.push({ label: 'Runner detail', value: nestedRunnerDetail });
  }
  return facts;
}

export function getBringUpFailureDiagnostics(step?: JobStep | null): string {
  const nestedRunner = getNestedRunnerFailureStep(step);
  const raw = String(
    nestedRunner?.err ||
      nestedRunner?.out ||
      step?.artifacts?.final?.detail ||
      step?.artifacts?.final?.error ||
      step?.artifacts?.init?.detail ||
      '',
  ).trim();
  if (raw) return raw.slice(0, 4000);
  const artifacts = step?.artifacts;
  if (artifacts && typeof artifacts === 'object') {
    try {
      return JSON.stringify(artifacts, null, 2).slice(0, 4000);
    } catch (_err) {
      return '';
    }
  }
  return '';
}

function toTrimmedString(value: any): string {
  return String(value ?? '').trim();
}

function joinDiagnosticParts(...values: any[]): string {
  return values
    .map((value) => toTrimmedString(value))
    .filter(Boolean)
    .join('\n')
    .trim();
}

export function getGuidedRecoveryFailureFacts(payload?: Record<string, any> | null): DetailFact[] {
  const data = payload && typeof payload === 'object' ? payload : {};
  const facts: DetailFact[] = [];
  const decommissionPhase = toTrimmedString(data.decommission_phase);
  const edgePhase = toTrimmedString(data.edge_phase);
  const edgeError = toTrimmedString(data.edge_error);
  const mqttError = toTrimmedString(data.mqtt_error);
  const edgeIp = toTrimmedString(data.edge_ip || data.edge_host);
  const attempts = Array.isArray(data.ssh_fallback_attempts) ? data.ssh_fallback_attempts : [];

  if (decommissionPhase) facts.push({ label: 'Decommission phase', value: decommissionPhase });
  if (edgePhase) facts.push({ label: 'Edge phase', value: edgePhase });
  if (edgeIp) facts.push({ label: 'Edge host', value: edgeIp });
  if (edgeError) facts.push({ label: 'Edge error', value: edgeError });
  if (mqttError) facts.push({ label: 'MQTT error', value: mqttError });
  if (attempts.length > 0) {
    const lastAttempt = attempts[attempts.length - 1] || {};
    const attemptIndex = toTrimmedString(lastAttempt.attempt || attempts.length);
    const reason = toTrimmedString(lastAttempt.reason);
    const runRc = toTrimmedString(lastAttempt.run_rc);
    const verifyRc = toTrimmedString(lastAttempt.verify_rc);
    const parts = [`attempt ${attemptIndex} failed`];
    if (reason) parts.push(reason);
    if (runRc) parts.push(`run_rc=${runRc}`);
    if (verifyRc) parts.push(`verify_rc=${verifyRc}`);
    facts.push({ label: 'Fallback attempt', value: parts.join('; ') });
  }
  return facts;
}

export function getGuidedRecoveryTechnicalDiagnostics(payload?: Record<string, any> | null): string {
  const data = payload && typeof payload === 'object' ? payload : {};
  const fallback = data.ssh_fallback && typeof data.ssh_fallback === 'object' ? data.ssh_fallback : {};
  const runResult = fallback.result && typeof fallback.result === 'object' ? fallback.result : {};
  const verifyResult =
    fallback.verify_result && typeof fallback.verify_result === 'object' ? fallback.verify_result : {};
  const blocks: string[] = [];

  const runText = joinDiagnosticParts(runResult.err, runResult.out);
  if (runText) {
    const rc = toTrimmedString(runResult.rc);
    blocks.push(`[edge_decommission${rc ? ` rc=${rc}` : ''}]\n${runText}`);
  }

  const verifyText = joinDiagnosticParts(verifyResult.err, verifyResult.out);
  if (verifyText) {
    const rc = toTrimmedString(verifyResult.rc);
    blocks.push(`[edge_decommission_verify${rc ? ` rc=${rc}` : ''}]\n${verifyText}`);
  }

  const detail = joinDiagnosticParts(data.detail, data.error, data.edge_error);
  if (!blocks.length && detail) {
    blocks.push(detail);
  }

  if (blocks.length > 0) return blocks.join('\n\n').slice(0, 4000);

  if (Object.keys(data).length > 0) {
    try {
      return JSON.stringify(data, null, 2).slice(0, 4000);
    } catch (_err) {
      return '';
    }
  }
  return '';
}

export function buildGuidedRecoveryFailureNotice(
  message: string,
  payload?: Record<string, any> | null,
): StatusNotice {
  const facts = getGuidedRecoveryFailureFacts(payload);
  const technical = getGuidedRecoveryTechnicalDiagnostics(payload);
  return {
    level: 'error',
    message,
    facts: facts.length > 0 ? facts : undefined,
    technical: technical || undefined,
  };
}

export function formatBringUpFailure(job?: CommissionJob | null, failedStep?: JobStep | null): string {
  const classification = getStepClassification(failedStep);
  if (classification?.summary) return classification.summary;

  const code = String(job?.error_code || '').trim();
  const detail = String(
    failedStep?.error_detail ||
      failedStep?.artifacts?.final?.detail ||
      failedStep?.artifacts?.final?.error ||
      job?.error_detail ||
      '',
  ).trim();
  const failedStepLabel = String(
    failedStep?.step_id ? (STEP_LABELS[String(failedStep.step_id)] || String(failedStep.step_id)) : '',
  ).trim();

  if (code === 'edge_contract_unreachable') {
    return (
      'Edge bootstrap is unreachable. This usually means the unit was factory-wiped or does not have the base ' +
      'edge runtime installed yet. Install/bootstrap the edge runtime first, then retry Bring Up.'
    );
  }

  if (code === 'edge_scope_unavailable') {
    return (
      'Edge health responded, but it did not report org/site scope yet. Finish bootstrap/onboarding on the unit ' +
      'first, then retry Bring Up.'
    );
  }

  if (code === 'edge_stale_scope') {
    return (
      'This unit is still bound to a different org/site than the one you selected. Decommission or factory-wipe ' +
      'the unit first, then retry Bring Up.'
    );
  }

  if (code === 'edge_stale_identity') {
    return (
      'This unit is still carrying a different device identity than the cloud device you selected. ' +
      'Decommission or factory-wipe the unit first, then retry Bring Up.'
    );
  }

  if (detail && failedStepLabel) {
    return `Bring-up failed during ${failedStepLabel}: ${detail}`;
  }
  if (detail) return `Bring-up failed: ${code || 'unknown_error'} - ${detail}`;
  return `Bring-up failed: ${code || 'unknown_error'}`;
}

export function formatBringUpRemediation(failedStep?: JobStep | null): string | null {
  const classification = getStepClassification(failedStep);
  const state = String(classification?.state || '').trim();

  if (state === 'stale_identity' || state === 'stale_scope') {
    return (
      'Run Guided Recovery below. It will factory-reset the edge, recreate the selected cloud device, ' +
      'and restart Bring Up automatically against the same device.'
    );
  }

  if (state === 'legacy_runtime') {
    return 'Bring Up can continue only after the edge reports a contract_v1 runtime identity contract.';
  }

  if (state === 'bootstrap_pending') {
    return 'Bring Up can continue once onboarding writes the selected org/site scope onto the edge runtime.';
  }

  return null;
}

export function canRunGuidedRecovery(failedStep?: JobStep | null): boolean {
  const state = String(getStepClassification(failedStep)?.state || '').trim();
  return state === 'stale_identity' || state === 'stale_scope';
}

export function formatStepSecondary(stepId: string, step?: JobStep | null): string {
  const classification = getStepClassification(step);
  if (classification?.summary) return classification.summary;

  const errorDetail = String(step?.error_detail || '').trim();
  if (errorDetail) return errorDetail;

  const errorCode = String(step?.error_code || '').trim();
  if (errorCode) return errorCode;

  const status = String(step?.status || '').trim().toLowerCase();
  if (!step || status === 'pending') {
    return STEP_PENDING_TEXT[stepId] || 'Waiting for previous bring-up steps.';
  }

  return stepId;
}
